import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./subscribe.css";
import { setPaySummary } from "../Pay/PayOverlay";

// Replaces the old subscribe view. Calls the SAME functions with the SAME values as before:
// onSubscribe / onAddToCart (price, plan, currency, publicationId, isPaid).
const BOOK_TYPE = "eb244fbc-ab2c-4a51-9b6f-034d987f352e";
const PLANS = [
  { key: "one-off", label: "Single edition", ngn: "oneOffFee", usd: ["oneOffDollarFee", "oneOffFeeDollarFee"], days: 1 },
  { key: "weekly", label: "Weekly", ngn: "weeklyFee", usd: ["weeklyDollarFee", "weeklyFeeDollarFee"], days: 7 },
  { key: "monthly", label: "Monthly", ngn: "monthlyFee", usd: ["monthlyDollarFee", "monthlyFeeDollarFee"], months: 1, popular: true },
  { key: "quarterly", label: "Every 3 months", ngn: "quarterlyFee", usd: ["quarterlyDollarFee", "quarterlyFeeDollarFee"], months: 3 },
  { key: "biannually", label: "Every 6 months", ngn: "biannuallyFee", usd: ["biannuallyDollarFee", "biannualDollarFee", "biannuallyFeeDollarFee"], months: 6 },
  { key: "annually", label: "Yearly", ngn: "annualFee", usd: ["annualDollarFee", "annualFeeDollarFee"], months: 12 },
];

const endDate = (p) => {
  const d = new Date();
  if (p.months) d.setMonth(d.getMonth() + p.months);
  else d.setDate(d.getDate() + p.days);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};
const money = (v, cur) =>
  cur === "NGN" ? `₦${Math.round(Number(v)).toLocaleString("en-NG")}` : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SubscribeSheet({ edition, requested, type, userLocation, onClose, onSubscribe, onAddToCart }) {
  const currency = userLocation === "Nigeria" ? "NGN" : "USD";
  const pub = edition && edition.publication;
  // Don't show the previous paper's plans while the new one loads.
  const ready = Boolean(pub) && (!requested || edition.id === requested.id || edition.publicationId === requested.publicationId);
  const isBook = type === "book" || (pub && pub.typeId === BOOK_TYPE);
  const isPaid = pub ? Boolean(pub.isPaid) : true;

  const plans = ready
    ? PLANS.filter((p) => (isBook ? p.key === "one-off" : true))
        .filter((p) => Number(pub[p.ngn]))
        .map((p) => {
          const usd = p.usd.map((k) => pub[k]).find((v) => Number(v));
          const price = currency === "NGN" ? Number(pub[p.ngn]) : Number(usd || 0);
          const days = p.months ? p.months * 30.4 : p.days;
          return { ...p, price, until: endDate(p), perDay: p.key !== "one-off" && price ? price / days : null };
        })
    : [];

  const [chosen, setChosen] = useState(null);
  useEffect(() => {
    if (!plans.length) return;
    if (!chosen || !plans.some((p) => p.key === chosen)) setChosen((plans.find((p) => p.popular) || plans[0]).key);
  }, [ready, plans.length]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const plan = plans.find((p) => p.key === chosen);
  const priceText = (p) => (!isPaid ? "Free" : money(p.price, currency));
  const fire = (fn) => {
    if (!plan) return;
    const fee = currency === "NGN" ? pub[plan.ngn] : plan.usd.map((k) => pub[k]).find((v) => Number(v));
    if (fn === onSubscribe) setPaySummary({ title: `${pub.name} · ${label(plan)}`, amount: plan.price });
    onClose();
    fn(fee, plan.key, currency, edition.publicationId, pub.isPaid);
  };
  const label = (p) => (isBook && p.key === "one-off" ? "Buy this book" : p.label);
  const payText = !plan ? "Choose a plan" : !isPaid ? "Get it free" : isBook ? `Buy for ${money(plan.price, currency)}` : `Pay ${money(plan.price, currency)}`;

  return createPortal(
    <div className="sb-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="sb-sheet" role="dialog" aria-modal="true" aria-labelledby="sb-title">
        <span className="sb-grab" aria-hidden="true" />
        <div className="sb-head">
          <span className="sb-cover">{edition && edition.image ? <img src={edition.image} alt="" /> : null}</span>
          <div className="sb-titles">
            <h2 id="sb-title" className="sb-title">{pub ? (isBook ? pub.name : `Subscribe to ${pub.name}`) : "Subscribe"}</h2>
            <p className="sb-sub">{isBook ? "Buy it once and read it any time." : "Read every new edition as soon as it’s published."}</p>
          </div>
          <button type="button" className="sb-close" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        {!ready ? (
          <p className="sb-loading">Loading plans…</p>
        ) : !plans.length ? (
          <p className="sb-loading">This paper has no plans on sale right now.</p>
        ) : (
          <>
            <fieldset className="sb-plans">
              <legend className="sb-legend">{isBook ? "Your purchase" : "Choose how long"}</legend>
              {plans.map((p) => (
                <label key={p.key} className={`sb-plan${chosen === p.key ? " on" : ""}`}>
                  <input type="radio" name="sb-plan" value={p.key} checked={chosen === p.key} onChange={() => setChosen(p.key)} />
                  <span className="sb-dot" aria-hidden="true" />
                  <span className="sb-info">
                    <span className="sb-name">{label(p)}{p.popular && plans.length > 2 ? <span className="sb-badge">Most popular</span> : null}</span>
                    {!isBook ? <span className="sb-until">Until {p.until}</span> : null}
                  </span>
                  <span className="sb-price">
                    <strong>{priceText(p)}</strong>
                    {isPaid && p.perDay ? <span>about {money(currency === "NGN" ? Math.round(p.perDay) : p.perDay, currency)} a day</span> : null}
                  </span>
                </label>
              ))}
            </fieldset>

            <div className="sb-foot">
              {plan ? (
                <div className="sb-summary"><span>{label(plan)}{!isBook ? ` · until ${plan.until}` : ""}</span><strong>{priceText(plan)}</strong></div>
              ) : null}
              <button type="button" className="sb-pay" disabled={!plan} onClick={() => fire(onSubscribe)}>{payText}</button>
              {isPaid && plan ? <button type="button" className="sb-cart" onClick={() => fire(onAddToCart)}>Add to cart instead</button> : null}
              <p className="sb-secure">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                Secure payment by card, bank transfer or USSD, via Paystack
              </p>
            </div>
          </>
        )}
      </section>
    </div>,
    document.body
  );
}
